import { ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, Typography, useEventCallback } from '@mui/material';
import { PmiButton } from 'shell/components/shared/button';
import { useFormik } from 'formik';
import { Fragment, memo, useEffect, useMemo, useRef, useState } from 'react';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import * as yup from 'yup';
import AccordionRequestAssessmentForm, {
  RequestAssessmentVM,
} from 'practice/components/detail/form/accept-messi/form';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { PracticeData } from 'shell/models/practice';
import { toast } from 'react-toastify';
import { TypeRegister } from 'practice/constants/type-register';
import {
  PracticeDraftBEStep,
  PracticeDraftStep,
} from 'practice/constants/practice-management-step';

interface AccordionRequestAssessmentProps {
  codeEnte?: string;
  yearId?: string;
  typeRegister?: string;
  progressive?: string;
  typePractice?: string;
  setDisabledAll: (value: boolean) => void;
  onNext: (step?: PracticeDraftStep) => void;
  onBack: (step?: PracticeDraftStep) => void;
  disabledAll?: boolean;
  iniziativa?: string;
  setIsAcceptMessi: (value: boolean) => void;
}

const AccordionRequestAssessmentComponent = ({
  codeEnte,
  yearId,
  typeRegister,
  progressive,
  disabledAll,
  iniziativa,
  onNext,
  onBack,
  setIsAcceptMessi,
}: AccordionRequestAssessmentProps): React.ReactElement => {
  const [open, setOpen] = useState(true);

  const [controlloCheck] = useHttpFetch<HttpResponse<PracticeData>, PracticeData>(
    `${__PRACTICE_API_URL__}/accertamento-messi/ulteriori-accertamenti`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {
          codiceEnte: codeEnte,
          tipoRegistro: typeRegister,
          idAnnoPratica: yearId ? Number.parseInt(yearId, 10) : undefined,
          progressivoPratica: progressive ? Number.parseInt(progressive, 10) : undefined,
        },
      },
      transformer: (response) => response.data.payload,
    }
  );

  const updateData = useHttpClient(`${__PRACTICE_API_URL__}/accertamento-messi/accertamento`, {
    options: { method: 'POST' },
  });

  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    ref.current?.scrollIntoView();
  }, []);

  const onSubmit = useEventCallback(() => {
    const data = {
      codiceEnte: codeEnte,
      tipoRegistro: typeRegister,
      idAnnoPratica: yearId ? Number.parseInt(yearId, 10) : undefined,
      progressivoPratica: progressive ? Number.parseInt(progressive, 10) : undefined,
      richiestaAccertamento: formContext.values.request ? '1' : '0',
      note: formContext.values.note || undefined,
      stepBozza: PracticeDraftBEStep.AccertamentoMessi,
      scala: formContext.values.scala || undefined,
      piano: formContext.values.piano || undefined,
      interno: formContext.values.interno || undefined,
    };

    (updateData({ data }) as ResponsePromise<void>).then(() => {
      toast.success('Operazione eseguita con successo');
      //setDisabledAll(true);
      setIsAcceptMessi(true);
      onNext(PracticeDraftStep.Recap);
    });
  });

  const formContext = useFormik<RequestAssessmentVM>({
    initialValues: {
      request:
        (controlloCheck.data as PracticeData)?.accertamentoMessi ||
        (controlloCheck.data as PracticeData)?.flagAttualeResidenza ||
        (controlloCheck.data as PracticeData)?.flagImmigrazione ||
        typeRegister === TypeRegister.Unavailable ||
        typeRegister === TypeRegister.UntraceableCensus ||
        false,
      scala: (controlloCheck.data as PracticeData)?.scala || '',
      interno: (controlloCheck.data as PracticeData)?.interno || '',
      piano: (controlloCheck.data as PracticeData)?.piano || '',
      note: (controlloCheck.data as PracticeData)?.noteOperazione || '',
    },
    validationSchema: yup.object().shape({}),
    onSubmit,
    enableReinitialize: true,
  });

  const disabled = useMemo(() => {
    if (disabledAll) {
      return true;
    }

    // if (iniziativa === TypeInitiative.Anpr || iniziativa === TypeInitiative.ElixForm) {
    //   return true;
    // }

    return false;
  }, [disabledAll, iniziativa]);

  return (
    <Fragment>
      <Accordion
        expanded={open}
        onChange={() => {
          setOpen(!open);
        }}
        ref={ref}
      >
        <AccordionSummary
          aria-controls="panel-request-assessment-content"
          id="panel-request-assessment-header"
          expandIcon={<ExpandMore />}
        >
          <Typography>Accertamento messi</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <AccordionRequestAssessmentForm
            values={formContext.values}
            typeRegister={typeRegister}
            onChange={formContext.setFieldValue}
            onChanges={formContext.setValues}
            errors={formContext.errors}
            touched={formContext.touched}
            disabled={disabled}
            disabledMessi={
              (controlloCheck.data as PracticeData)?.flagAttualeResidenza ||
              (controlloCheck.data as PracticeData)?.flagImmigrazione ||
              false
            }
          />
          <div className="pmi-pt-3 pmi-d-flex pmi-justify-content-center">
            <div className="pmi-w-25 pmi-w-lg-15">
              <PmiButton
                variant="outlined"
                fullWidth
                color="cdmBrand"
                onClick={() => onBack(PracticeDraftStep.Documents)}
              >
                Indietro
              </PmiButton>
            </div>
            <div className="pmi-pl-2 pmi-w-25 pmi-w-lg-15">
              <PmiButton
                color="cdmBrandThird"
                variant="outlined"
                onClick={() => formContext.handleSubmit()}
                id="return"
                disabled={disabled}
                fullWidth
              >
                Avanti
                {/** ULTIMA OPERAZIONE => SOLLECITA ACCERTAMENTO */}
              </PmiButton>
            </div>
          </div>
        </AccordionDetails>
      </Accordion>
    </Fragment>
  );
};

AccordionRequestAssessmentComponent.displayName = 'Accordion request assessment component';

const AccordionRequestAssessment = memo(AccordionRequestAssessmentComponent);

export default AccordionRequestAssessment;
